import { useEffect, useRef, useState } from "react";
import { Brain, CaretRight } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { ThinkingLabel } from "../terminal-v2/ThinkingLabel";

/**
 * Collapsible "Thought process" block. Shown above an assistant/coworker
 * message body. **Stays expanded by default** for the entire turn — only
 * collapses if the user explicitly clicks the chevron. This matches the
 * locked UX spec: the user wants to read the reasoning *while* the
 * answer streams, not have it hidden behind a click.
 *
 * Used by the coworker DM streaming bubble (`StreamingDmBubble`), reading
 * thinking from the ephemeral `agentDmStreamStore` populated by SSE events.
 * The thinking text comes from in-memory state, never from the database —
 * `messages.agent_thinking` no longer exists as a column.
 */
export function ThinkingSection({
  text,
  isStreaming,
}: {
  text: string;
  /**
   * True only during the active *thinking phase* (no answer text yet).
   * Drives the header label (spinner + "Thinking…" vs brain +
   * "Thought process") but does NOT control the expanded state — the
   * chip stays expanded across thinking, answering, and after stream
   * end, until the user manually collapses it.
   */
  isStreaming: boolean;
}) {
  // null = user hasn't toggled. Default is **expanded** (true) so the
  // reasoning is visible without a click; the chip only collapses when
  // the user explicitly clicks the chevron.
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  const expanded = userOverride ?? true;
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom while streaming
  useEffect(() => {
    if (!isStreaming || !expanded) return;
    const el = contentRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [text, isStreaming, expanded]);

  return (
    // Borderless card — no outline. Same width as the chat body
    // (not edge-to-edge): the chip's natural block width matches
    // the surrounding markdown column, and the header hugs its
    // own content so the collapsed state is narrow and unobtrusive.
    <div className="mb-1.5 rounded-xl bg-foreground/[0.03]">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setUserOverride((prev) => !(prev ?? true))}
        className="h-auto w-fit max-w-full justify-start gap-1.5 rounded-xl px-3 py-2 text-sm leading-relaxed font-normal text-muted-foreground/80 hover:bg-foreground/[0.04] hover:text-foreground"
      >
        <CaretRight
          size={10}
          className={`transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        {isStreaming ? (
          // Streaming: AnoSpinner replaces the previous Brain icon +
          // blue pulsing dot pair (one mark instead of two).
          <>
            <LoadingIcon size={12} className="text-muted-foreground" />
            <ThinkingLabel className="text-sm leading-relaxed font-normal text-muted-foreground" />
          </>
        ) : (
          <>
            <Brain size={12} />
            Thought process
          </>
        )}
      </Button>
      {expanded && (
        <div
          ref={contentRef}
          // Cap the visible reasoning at ~3 lines (3 × text-sm
          // leading-relaxed ≈ 4.5rem). Longer reasoning scrolls within
          // the fixed-size chip; the existing auto-scroll effect above
          // pins the latest line into view. Before this cap the chip
          // grew unbounded as the model reasoned and the chat body
          // jumped every time a new line arrived.
          className="max-h-[4.5rem] overflow-y-auto whitespace-pre-wrap px-3 pb-3 text-sm leading-relaxed text-muted-foreground/80"
        >
          {text}
        </div>
      )}
    </div>
  );
}
